import React, { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import InlineSpinner from '../../shared/InlineSpinner';

import LambdaLogStreamPanel from '../../shared/LambdaLogStreamPanel';
import { useLambdaLogStream } from '../../../utils/useLambdaLogStream';
import type { LambdaLogPollFn } from '../../../utils/lambdaLogStreamPoller';
import { dispatchToast, dispatchToastError } from '../utils/eventsOverviewHelpers';

type ReportingConfigRow = {
  info: string;
  offset_from_utc: string;
  date: string;
  dist_rule: string;
  trans_lat: string;
  trans_long: string;
  radius: string;
  opt_trans2_lat: string;
  opt_trans2_long: string;
  opt_trans2_radius: string;
  opt_nonpolice_name: string;
  opt_nonpolice_lat: string;
  opt_nonpolice_lon: string;
  opt_nonpolice_radius: string;
};

type AthleteCoverageRow = {
  deviceNum: string;
  bibNum: string;
  athleteName: string;
  rearDeviceId: string;
  frontDeviceId?: string;
  hasRearRacelog: boolean;
  hasRearDebuglog: boolean;
  hasFrontRacelog: boolean;
  hasFrontDebuglog: boolean;
  missingLogs: string[];
};

type ReportingRunRow = {
  runId: string;
  status: string;
  createdAt?: number;
  updatedAt?: number;
  error?: string | null;
  artifacts?: Array<{
    artifactId: string;
    fileName?: string;
  }>;
};

type Props = {
  eventId: string;
  raceId: string;
  statusReloadKey?: string | number;
};

const EMPTY_ROW: ReportingConfigRow = {
  info: '',
  offset_from_utc: '',
  date: '',
  dist_rule: '',
  trans_lat: '',
  trans_long: '',
  radius: '',
  opt_trans2_lat: '',
  opt_trans2_long: '',
  opt_trans2_radius: '',
  opt_nonpolice_name: '',
  opt_nonpolice_lat: '',
  opt_nonpolice_lon: '',
  opt_nonpolice_radius: '',
};

function normalizeRows(rawRows: unknown): ReportingConfigRow[] {
  if (!Array.isArray(rawRows) || rawRows.length < 1) return [{ ...EMPTY_ROW }];
  const rows = rawRows
    .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
    .map((row) => ({
      info: String(row.info ?? '').trim(),
      offset_from_utc: String(row.offset_from_utc ?? '').trim(),
      date: String(row.date ?? '').trim(),
      dist_rule: String(row.dist_rule ?? '').trim(),
      trans_lat: String(row.trans_lat ?? '').trim(),
      trans_long: String(row.trans_long ?? '').trim(),
      radius: String(row.radius ?? '').trim(),
      opt_trans2_lat: String(row.opt_trans2_lat ?? '').trim(),
      opt_trans2_long: String(row.opt_trans2_long ?? '').trim(),
      opt_trans2_radius: String(row.opt_trans2_radius ?? '').trim(),
      opt_nonpolice_name: String(row.opt_nonpolice_name ?? '').trim(),
      opt_nonpolice_lat: String(row.opt_nonpolice_lat ?? '').trim(),
      opt_nonpolice_lon: String(row.opt_nonpolice_lon ?? '').trim(),
      opt_nonpolice_radius: String(row.opt_nonpolice_radius ?? '').trim(),
    }))
    .filter((row) => Object.values(row).some((v) => v.length > 0));
  return rows.length > 0 ? rows : [{ ...EMPTY_ROW }];
}

function formatEpoch(epoch?: number): string {
  if (!epoch) return '—';
  const d = new Date(epoch * 1000);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;
/** ~10 table body rows visible before scrolling */
const COVERAGE_TABLE_MAX_HEIGHT = 'max-h-[22rem]';

function fmtLogAge(epochMs: number | undefined, nowMs: number): string {
  if (epochMs == null || epochMs <= 0) return '—';
  const diffMs = nowMs - epochMs;
  if (diffMs < 0) return 'just now';
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(diffMs / ONE_HOUR_MS);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(diffMs / ONE_DAY_MS);
  return `${days}d ago`;
}

function formatLogWriteTime(epochMs: number | undefined): string {
  if (epochMs == null || epochMs <= 0) return '—';
  const d = new Date(epochMs);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function logStaleTier(epochMs: number | undefined, nowMs: number, hasLog: boolean): 'missing' | 'stale_day' | 'stale_hour' | 'fresh' {
  if (!hasLog || epochMs == null || epochMs <= 0) return 'missing';
  const diff = nowMs - epochMs;
  if (diff > ONE_DAY_MS) return 'stale_day';
  if (diff > ONE_HOUR_MS) return 'stale_hour';
  return 'fresh';
}

function coverageRowClass(tier: ReturnType<typeof logStaleTier>, excluded: boolean): string {
  const base = 'border-b border-slate-100 last:border-0 dark:border-slate-800';
  if (excluded) return `${base} opacity-40`;
  if (tier === 'missing' || tier === 'stale_day') return `${base} bg-red-50 dark:bg-red-950/20`;
  if (tier === 'stale_hour') return `${base} bg-amber-50 dark:bg-amber-950/20`;
  return base;
}

function coverageSortRank(athlete: AthleteCoverageRow): number {
  let rank = 0;
  if (!athlete.hasRearRacelog) rank += 4;
  if (!athlete.hasRearDebuglog) rank += 2;
  return rank;
}

type DeviceLogTimes = {
  raceEpochMs?: number;
  debugEpochMs?: number;
};

/** Strong visible pulse on the completed run card (matches generate-button emphasis). */
const RUN_HIGHLIGHT_CLASS = [
  'animate-pulse',
  '!border-blue-400 !bg-blue-50/90',
  'dark:!border-blue-400 dark:!bg-blue-950/70',
  'ring-2 ring-blue-400 ring-offset-2 ring-offset-white',
  'dark:ring-blue-400 dark:ring-offset-slate-900',
  'shadow-[0_0_16px_rgba(59,130,246,0.55)]',
  'dark:shadow-[0_0_20px_rgba(96,165,250,0.45)]',
].join(' ');

const GENERATE_BUTTON_PULSE_CLASS = [
  'animate-pulse',
  '!border-emerald-500 !bg-emerald-600 !text-white',
  'dark:!border-emerald-400 dark:!bg-emerald-500',
  'ring-2 ring-emerald-300/90 ring-offset-2',
  'dark:ring-emerald-500/80 dark:ring-offset-slate-900',
  'shadow-[0_0_0_4px_rgba(16,185,129,0.28)]',
  'hover:!bg-emerald-700 dark:hover:!bg-emerald-400',
].join(' ');

function isRunInProgress(status: string): boolean {
  const s = String(status ?? '').trim().toUpperCase();
  return s === 'PENDING' || s === 'RUNNING';
}

export default function RaceReportingPanel({ eventId, raceId, statusReloadKey }: Props): ReactElement {
  const [rows, setRows] = useState<ReportingConfigRow[]>([{ ...EMPTY_ROW }]);
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState('');
  const [configSaved, setConfigSaved] = useState(false);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState('');
  const [runs, setRuns] = useState<ReportingRunRow[]>([]);
  const [generating, setGenerating] = useState(false);
  const [deletingRunId, setDeletingRunId] = useState('');
  const [streamRunId, setStreamRunId] = useState('');
  const [highlightRunId, setHighlightRunId] = useState('');
  const [coverage, setCoverage] = useState<AthleteCoverageRow[]>([]);
  const highlightedRunRef = useRef<HTMLDivElement | null>(null);
  const focusedCompletionRunRef = useRef('');
  const scrollHighlightOnceRef = useRef('');
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageError, setCoverageError] = useState('');
  const [excludedDeviceNums, setExcludedDeviceNums] = useState<Set<string>>(new Set());
  const [logTimesByDeviceId, setLogTimesByDeviceId] = useState<Record<string, DeviceLogTimes>>({});
  const [logTimesLoading, setLogTimesLoading] = useState(false);

  const validateRows = useCallback((inputRows: ReportingConfigRow[]): string | null => {
    const first = inputRows[0] ?? EMPTY_ROW;
    const required: Array<keyof ReportingConfigRow> = [
      'info',
      'offset_from_utc',
      'date',
      'dist_rule',
      'trans_lat',
      'trans_long',
      'radius',
    ];
    for (const key of required) {
      if (!String(first[key] ?? '').trim()) {
        return `Missing required field: ${key}`;
      }
    }
    return null;
  }, []);

  const loadConfig = useCallback(async (): Promise<void> => {
    setConfigLoading(true);
    setConfigError('');
    try {
      const res = await window.eventsAPI.getRaceReportingConfiguration({ eventId, raceId });
      if (!res?.ok) {
        throw new Error(String(res?.error ?? 'Failed to load reporting configuration'));
      }
      const responseRows = normalizeRows((res?.data as { configuration?: { rows?: unknown } } | undefined)?.configuration?.rows);
      setRows(responseRows);
      setConfigSaved(true);
    } catch (err: unknown) {
      setConfigError((err as Error)?.message ?? 'Failed to load reporting configuration');
      setRows([{ ...EMPTY_ROW }]);
      setConfigSaved(false);
    } finally {
      setConfigLoading(false);
    }
  }, [eventId, raceId]);

  const loadCoverage = useCallback(async (): Promise<void> => {
    setCoverageLoading(true);
    setCoverageError('');
    try {
      const res = await window.eventsAPI.getRaceReportingAthleteCoverage(eventId, raceId);
      if (!res?.ok) throw new Error(String(res?.error ?? 'Failed to load coverage'));
      const athletes = (res.data as { athletes?: unknown[] } | undefined)?.athletes;
      setCoverage(Array.isArray(athletes) ? (athletes as AthleteCoverageRow[]) : []);
    } catch (err: unknown) {
      setCoverageError((err as Error)?.message ?? 'Failed to load athlete log coverage');
      setCoverage([]);
    } finally {
      setCoverageLoading(false);
    }
  }, [eventId, raceId]);

  const loadRuns = useCallback(async (): Promise<void> => {
    setRunsLoading(true);
    setRunsError('');
    try {
      const res = await window.eventsAPI.listRaceReportingRuns(eventId, raceId, { limit: 25 });
      if (!res?.ok) {
        throw new Error(String(res?.error ?? 'Failed to load reporting runs'));
      }
      const list = Array.isArray((res?.data as { runs?: unknown[] } | undefined)?.runs)
        ? ((res.data as { runs: unknown[] }).runs as ReportingRunRow[])
        : [];
      setRuns(list);
    } catch (err: unknown) {
      setRunsError((err as Error)?.message ?? 'Failed to load reporting runs');
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  }, [eventId, raceId]);

  useEffect(() => {
    void loadConfig();
    void loadCoverage();
    void loadRuns();
  }, [loadConfig, loadCoverage, loadRuns, statusReloadKey]);

  useEffect(() => {
    if (!eventId || coverage.length < 1) {
      setLogTimesByDeviceId({});
      return undefined;
    }
    let cancelled = false;
    setLogTimesLoading(true);
    void (async () => {
      const rearIds = [...new Set(
        coverage.map((a) => String(a.rearDeviceId ?? '').trim()).filter(Boolean),
      )];
      const next: Record<string, DeviceLogTimes> = {};
      await Promise.all(rearIds.map(async (deviceId) => {
        try {
          const [raceFiles, debugFiles] = await Promise.all([
            window.fleetAPI.listLogFiles({ deviceId, logType: 'race', eventId }),
            window.fleetAPI.listLogFiles({ deviceId, logType: 'debug', eventId }),
          ]);
          const pickLatest = (files: Array<{ lastModifiedEpochMs?: number }>) => files.reduce(
            (best, f) => ((f.lastModifiedEpochMs ?? 0) > (best.lastModifiedEpochMs ?? 0) ? f : best),
            { lastModifiedEpochMs: 0 },
          ).lastModifiedEpochMs;
          next[deviceId] = {
            raceEpochMs: pickLatest(raceFiles) || undefined,
            debugEpochMs: pickLatest(debugFiles) || undefined,
          };
        } catch {
          next[deviceId] = {};
        }
      }));
      if (!cancelled) setLogTimesByDeviceId(next);
    })().finally(() => {
      if (!cancelled) setLogTimesLoading(false);
    });
    return () => { cancelled = true; };
  }, [coverage, eventId, statusReloadKey]);

  const sortedCoverage = useMemo(
    () => [...coverage].sort((a, b) => {
      const rankDiff = coverageSortRank(b) - coverageSortRank(a);
      if (rankDiff !== 0) return rankDiff;
      return String(a.bibNum ?? '').localeCompare(String(b.bibNum ?? ''), undefined, { numeric: true });
    }),
    [coverage],
  );

  const nowMs = useMemo(() => Date.now(), [coverage, logTimesByDeviceId, logTimesLoading]);

  const hasActiveRun = useMemo(
    () => runs.some((run) => run.status === 'PENDING' || run.status === 'RUNNING'),
    [runs],
  );

  const streamRunStatus = useMemo(() => {
    if (!streamRunId) return '';
    return String(runs.find((run) => run.runId === streamRunId)?.status ?? '').toUpperCase();
  }, [runs, streamRunId]);

  /** Poll list + logs only for the run we just started (not every historical PENDING row). */
  const streamRunInProgress = Boolean(
    streamRunId
    && (!streamRunStatus || streamRunStatus === 'PENDING' || streamRunStatus === 'RUNNING'),
  );

  /** Runner replaces coverage + outputs lists until the active run finishes. */
  const showRunnerLogs = Boolean(streamRunId && streamRunInProgress);

  const streamPollFn = useMemo<LambdaLogPollFn | null>(() => {
    if (!streamRunId) return null;
    return async (nextToken) => {
      const res = await window.eventsAPI.getRaceReportingRunLogs(eventId, raceId, streamRunId, {
        nextToken: nextToken ?? undefined,
      });
      if (!res?.ok) {
        throw new Error(String(res?.error ?? 'Failed to load run logs'));
      }
      const data = (res.data ?? {}) as {
        events?: Array<{ timestampMs?: number; message?: string; ingestionTimeMs?: number }>;
        nextToken?: string | null;
      };
      return {
        events: (data.events ?? []).map((ev) => ({
          timestampMs: Number(ev.timestampMs ?? 0),
          message: String(ev.message ?? ''),
          ingestionTimeMs: ev.ingestionTimeMs,
        })),
        nextToken: data.nextToken ?? null,
      };
    };
  }, [eventId, raceId, streamRunId]);

  const shouldStopStream = useCallback(async (): Promise<boolean> => {
    if (!streamRunId) return true;
    return streamRunStatus === 'COMPLETE' || streamRunStatus === 'FAILED';
  }, [streamRunId, streamRunStatus]);

  const { lines: logLines, streamError, streaming } = useLambdaLogStream(
    streamPollFn,
    streamRunInProgress,
    shouldStopStream,
  );

  /** Runs poll sets COMPLETE before the log poller can — highlight from status, not stream end. */
  useEffect(() => {
    const runId = streamRunId;
    if (!runId) return undefined;
    const status = streamRunStatus;
    const isTerminal = status === 'COMPLETE' || status === 'FAILED';
    if (!isTerminal || focusedCompletionRunRef.current === runId) return undefined;
    focusedCompletionRunRef.current = runId;
    setHighlightRunId(runId);
    void loadRuns();
    return undefined;
  }, [streamRunId, streamRunStatus, loadRuns]);

  useEffect(() => {
    if (!highlightRunId) return undefined;
    const id = window.setTimeout(() => setHighlightRunId(''), 12_000);
    return () => window.clearTimeout(id);
  }, [highlightRunId]);

  /** One smooth scroll to the highlighted card after it is in the list. */
  useEffect(() => {
    if (!highlightRunId) return undefined;
    if (!runs.some((run) => run.runId === highlightRunId)) return undefined;
    if (scrollHighlightOnceRef.current === highlightRunId) return undefined;
    scrollHighlightOnceRef.current = highlightRunId;
    const id = window.setTimeout(() => {
      highlightedRunRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    return () => window.clearTimeout(id);
  }, [highlightRunId, runs]);

  useEffect(() => {
    if (streamRunId) return;
    const active = runs.find((run) => isRunInProgress(run.status));
    if (active?.runId) setStreamRunId(active.runId);
  }, [runs, streamRunId]);

  const runsPollBusyRef = useRef(false);
  useEffect(() => {
    if (!streamRunInProgress) return undefined;
    const id = window.setInterval(() => {
      if (runsPollBusyRef.current) return;
      runsPollBusyRef.current = true;
      void loadRuns().finally(() => {
        runsPollBusyRef.current = false;
      });
    }, 5000);
    return () => window.clearInterval(id);
  }, [streamRunInProgress, loadRuns]);

  const setCell = (idx: number, key: keyof ReportingConfigRow, value: string): void => {
    setRows((prev) => prev.map((row, rowIdx) => (rowIdx === idx ? { ...row, [key]: value } : row)));
    setConfigSaved(false);
  };

  const addRow = (): void => {
    setRows((prev) => [...prev, { ...EMPTY_ROW }]);
    setConfigSaved(false);
  };

  const removeRow = (idx: number): void => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, rowIdx) => rowIdx !== idx);
    });
    setConfigSaved(false);
  };

  const saveConfig = async (): Promise<void> => {
    const validationError = validateRows(rows);
    if (validationError) {
      setConfigError(validationError);
      return;
    }
    setConfigSaving(true);
    setConfigError('');
    try {
      const res = await window.eventsAPI.submitRaceReportingConfiguration(eventId, {
        raceId,
        configuration: { rows },
      });
      if (!res?.ok) {
        throw new Error(String(res?.error ?? 'Failed to save reporting configuration'));
      }
      setRows(normalizeRows((res?.data as { configuration?: { rows?: unknown } } | undefined)?.configuration?.rows ?? rows));
      setConfigSaved(true);
    } catch (err: unknown) {
      setConfigError((err as Error)?.message ?? 'Failed to save reporting configuration');
      setConfigSaved(false);
    } finally {
      setConfigSaving(false);
    }
  };

  const generateReports = async (): Promise<void> => {
    setGenerating(true);
    setRunsError('');
    try {
      const res = await window.eventsAPI.createRaceReportingRun(eventId, raceId, {
        excludedDeviceNums: Array.from(excludedDeviceNums),
      });
      if (!res?.ok) {
        throw new Error(String(res?.error ?? 'Failed to start report generation'));
      }
      const runId = String((res.data as { runId?: string } | undefined)?.runId ?? '').trim();
      if (runId) {
        focusedCompletionRunRef.current = '';
        scrollHighlightOnceRef.current = '';
        setHighlightRunId('');
        setStreamRunId(runId);
      }
      await loadRuns();
    } catch (err: unknown) {
      setRunsError((err as Error)?.message ?? 'Failed to start report generation');
    } finally {
      setGenerating(false);
    }
  };

  const toggleExclude = (deviceNum: string): void => {
    setExcludedDeviceNums((prev) => {
      const next = new Set(prev);
      if (next.has(deviceNum)) next.delete(deviceNum); else next.add(deviceNum);
      return next;
    });
  };

  const downloadArtifact = async (runId: string, artifactId: string): Promise<void> => {
    const res = await window.eventsAPI.getRaceReportingRunArtifactDownloadUrl(eventId, raceId, runId, artifactId);
    if (!res?.ok) {
      setRunsError(String(res?.error ?? 'Failed to get artifact URL'));
      return;
    }
    const url = String((res?.data as { url?: string } | undefined)?.url ?? '').trim();
    if (!url) {
      setRunsError('Artifact URL is missing');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const deleteRun = async (runId: string): Promise<void> => {
    setDeletingRunId(runId);
    try {
      const res = await window.eventsAPI.deleteRaceReportingRun(eventId, raceId, runId);
      if (!res?.ok) {
        const msg = String(res?.error ?? 'Failed to delete reporting run');
        setRunsError(msg);
        dispatchToast('toast-error', msg);
        return;
      }
      if (streamRunId === runId) setStreamRunId('');
      await loadRuns();
      dispatchToast('toast-info', `Reporting run ${runId} deleted`);
    } catch (err: unknown) {
      dispatchToastError(err, 'Failed to delete reporting run');
    } finally {
      setDeletingRunId('');
    }
  };

  const includedAthletes = coverage.filter((a) => !excludedDeviceNums.has(a.deviceNum));
  const allIncludedHaveLogs = includedAthletes.length > 0 && includedAthletes.every((a) => a.hasRearRacelog);
  const canGenerate = configSaved && allIncludedHaveLogs && !hasActiveRun && !configSaving && !generating;
  const pulseGenerateButton = canGenerate && !showRunnerLogs;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Race reporting config (`race_config.csv`)</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              onClick={addRow}
              disabled={configLoading || configSaving}
            >
              Add non-police row
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-md border border-blue-300 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-800 hover:bg-blue-100 disabled:opacity-50 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-100"
              onClick={() => void saveConfig()}
              disabled={configLoading || configSaving}
            >
              {configSaving ? (
                <>
                  <InlineSpinner size="xs" variant="current" />
                  Saving…
                </>
              ) : (
                'Save config'
              )}
            </button>
          </div>
        </div>
        {configLoading ? (
          <p className="text-xs text-slate-500 dark:text-slate-400">Loading reporting config…</p>
        ) : (
          <div className="space-y-2">
            {rows.map((row, idx) => (
              <div key={`report-row-${idx}`} className="rounded-lg border border-slate-200 p-2 dark:border-slate-700">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {idx === 0 ? 'Base row' : `Optional row ${idx + 1}`}
                  </p>
                  {idx > 0 ? (
                    <button
                      type="button"
                      className="rounded border border-rose-300 px-2 py-0.5 text-[10px] font-semibold text-rose-700 hover:bg-rose-50 dark:border-rose-700 dark:text-rose-300"
                      onClick={() => removeRow(idx)}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                  {Object.keys(EMPTY_ROW).map((rawKey) => {
                    const key = rawKey as keyof ReportingConfigRow;
                    return (
                      <label key={`${idx}-${key}`} className="flex flex-col gap-1">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{key}</span>
                        <input
                          value={row[key]}
                          onChange={(e) => setCell(idx, key, e.target.value)}
                          className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-800 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        {configError ? (
          <p className="mt-2 text-xs text-rose-700 dark:text-rose-300">{configError}</p>
        ) : (
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            {configSaved ? 'Configuration saved to race-scoped storage.' : 'Unsaved changes.'}
          </p>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Generate reports</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {showRunnerLogs
              ? 'Report generation in progress — live worker output below.'
              : coverageLoading
                ? 'Loading athlete log coverage…'
                : `${includedAthletes.length} athlete${includedAthletes.length !== 1 ? 's' : ''} included${excludedDeviceNums.size > 0 ? ` · ${excludedDeviceNums.size} excluded` : ''}`}
          </p>
        </div>

        {!coverageLoading && coverage.length > 0 ? (
          <>
            <div className="mb-2 flex flex-wrap items-center gap-3 text-[10px]">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-400" />
                <span className="text-slate-600 dark:text-slate-300">Race log &gt; 1 hour old</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-2.5 w-2.5 rounded-sm bg-red-400" />
                <span className="text-slate-600 dark:text-slate-300">Missing or &gt; 1 day old</span>
              </span>
            </div>
            <div className={`overflow-auto rounded-lg border border-slate-200 dark:border-slate-700 ${COVERAGE_TABLE_MAX_HEIGHT}`}>
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800/95">
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="px-2 py-1.5 text-left font-semibold text-slate-600 dark:text-slate-300">Bib</th>
                    <th className="px-2 py-1.5 text-left font-semibold text-slate-600 dark:text-slate-300">Athlete</th>
                    <th className="px-2 py-1.5 text-left font-semibold text-slate-600 dark:text-slate-300">Device</th>
                    <th className="px-2 py-1.5 text-center font-semibold text-slate-600 dark:text-slate-300">Race</th>
                    <th className="px-2 py-1.5 text-center font-semibold text-slate-600 dark:text-slate-300">Debug</th>
                    <th className="px-2 py-1.5 text-left font-semibold text-slate-600 dark:text-slate-300">Latest race log</th>
                    <th className="px-2 py-1.5 text-right font-semibold text-slate-600 dark:text-slate-300">Age</th>
                    <th className="px-2 py-1.5 text-right font-semibold text-slate-600 dark:text-slate-300"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCoverage.map((athlete) => {
                    const excluded = excludedDeviceNums.has(athlete.deviceNum);
                    const critical = !athlete.hasRearRacelog;
                    const logTimes = logTimesByDeviceId[String(athlete.rearDeviceId ?? '').trim()] ?? {};
                    const raceEpochMs = athlete.hasRearRacelog ? logTimes.raceEpochMs : undefined;
                    const staleTier = logStaleTier(raceEpochMs, nowMs, athlete.hasRearRacelog);
                    return (
                      <tr
                        key={athlete.deviceNum}
                        className={coverageRowClass(staleTier, excluded)}
                      >
                        <td className="px-2 py-1.5 font-mono text-slate-700 dark:text-slate-300">{athlete.bibNum}</td>
                        <td className="px-2 py-1.5 text-slate-800 dark:text-slate-100">{athlete.athleteName}</td>
                        <td className="px-2 py-1.5 font-mono text-slate-500 dark:text-slate-400">{athlete.deviceNum}</td>
                        <td className="px-2 py-1.5 text-center">
                          {athlete.hasRearRacelog
                            ? <span className="text-emerald-600 dark:text-emerald-400">✓</span>
                            : <span className="font-bold text-rose-600 dark:text-rose-400">✗</span>}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          {athlete.hasRearDebuglog
                            ? <span className="text-emerald-600 dark:text-emerald-400">✓</span>
                            : <span className="font-bold text-rose-600 dark:text-rose-400">✗</span>}
                        </td>
                        <td className="px-2 py-1.5 text-slate-600 dark:text-slate-300">
                          {logTimesLoading
                            ? '…'
                            : formatLogWriteTime(raceEpochMs)}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-slate-600 dark:text-slate-300">
                          {logTimesLoading ? '…' : fmtLogAge(raceEpochMs, nowMs)}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {critical || excluded ? (
                            <button
                              type="button"
                              onClick={() => toggleExclude(athlete.deviceNum)}
                              className={`rounded border px-2 py-0.5 text-[10px] font-semibold ${
                                excluded
                                  ? 'border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300'
                                  : 'border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-700 dark:text-rose-300'
                              }`}
                            >
                              {excluded ? 'Re-include' : 'Exclude'}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
        {coverageError ? (
          <p className="mb-2 text-xs text-rose-700 dark:text-rose-300">{coverageError}</p>
        ) : null}

        {!showRunnerLogs && !configSaved ? (
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">Save race reporting config before generating.</p>
        ) : null}
        {!showRunnerLogs && !allIncludedHaveLogs && includedAthletes.length > 0 ? (
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
            Some included athletes are missing rear race logs — exclude them or collect their logs before generating.
          </p>
        ) : null}
        {!showRunnerLogs && includedAthletes.length === 0 ? (
          <p className="mt-2 text-xs text-rose-700 dark:text-rose-300">All athletes excluded — re-include at least one to generate.</p>
        ) : null}

        {!showRunnerLogs ? (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={() => void generateReports()}
              disabled={!canGenerate && !generating}
              className={[
                'inline-flex min-w-[14rem] items-center justify-center gap-2 rounded-xl border-2 px-8 py-3 text-sm font-bold transition-all',
                'border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100',
                'disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-emerald-50',
                'dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100 dark:hover:bg-emerald-950/60',
                pulseGenerateButton ? GENERATE_BUTTON_PULSE_CLASS : '',
              ].join(' ')}
            >
              {generating ? <InlineSpinner variant="accent" /> : null}
              {generating ? 'Starting…' : 'Generate reports'}
            </button>
          </div>
        ) : (
          <div className="mt-4">
            <LambdaLogStreamPanel
              title={`Report worker (${streamRunId})`}
              lines={logLines}
              active={streaming || streamRunInProgress}
              error={streamError || undefined}
              emptyHint="Waiting for CloudWatch log lines from the reporting worker…"
            />
            {streamRunStatus ? (
              <p className="mt-1 text-[10px] text-slate-500 dark:text-slate-400">
                Run status: {streamRunStatus}
              </p>
            ) : (
              <p className="mt-1 text-[10px] text-slate-500 dark:text-slate-400">Run status: starting…</p>
            )}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Generated outputs</h3>
        {runsLoading ? (
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Loading runs…</p>
        ) : runs.length < 1 ? (
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">No report runs yet.</p>
        ) : (
          <div className="mt-2 space-y-2">
            {runs.map((run) => (
              <div
                key={run.runId}
                ref={run.runId === highlightRunId ? highlightedRunRef : undefined}
                className={[
                  'rounded border border-slate-200 px-3 py-2 text-xs dark:border-slate-700',
                  run.runId === highlightRunId ? RUN_HIGHLIGHT_CLASS : '',
                ].join(' ')}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-semibold text-slate-800 dark:text-slate-100">{run.runId}</div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full border border-slate-300 px-2 py-0.5 text-[10px] font-semibold dark:border-slate-600">
                      {run.status}
                    </span>
                    {(run.status === 'COMPLETE' || run.status === 'FAILED') ? (
                      <button
                        type="button"
                        disabled={deletingRunId === run.runId}
                        onClick={() => void deleteRun(run.runId)}
                        className="rounded border border-rose-300 px-2 py-0.5 text-[10px] font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50 dark:border-rose-700 dark:text-rose-300 dark:hover:bg-rose-950/30"
                      >
                        {deletingRunId === run.runId ? 'Deleting…' : 'Delete'}
                      </button>
                    ) : null}
                  </div>
                </div>
                <p className="mt-1 text-slate-500 dark:text-slate-400">
                  Started: {formatEpoch(run.createdAt)} · Updated: {formatEpoch(run.updatedAt)}
                </p>
                {run.error ? (
                  <p className="mt-1 text-rose-700 dark:text-rose-300">{run.error}</p>
                ) : null}
                {Array.isArray(run.artifacts) && run.artifacts.length > 0 ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {run.artifacts.map((artifact) => (
                      <button
                        key={`${run.runId}-${artifact.artifactId}`}
                        type="button"
                        className="rounded border border-blue-300 bg-blue-50 px-2 py-1 text-[10px] font-semibold text-blue-800 hover:bg-blue-100 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-100"
                        onClick={() => void downloadArtifact(run.runId, artifact.artifactId)}
                      >
                        Download {artifact.fileName || artifact.artifactId}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
        {runsError ? <p className="mt-2 text-xs text-rose-700 dark:text-rose-300">{runsError}</p> : null}
      </div>
    </div>
  );
}
